import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet, getRecentBetsOfType } from '../services/userDataService'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { MARKET_TYPE_MAPPING } from '../config/markets'

// A bonus conversion is hedged, so its profit is known the moment it is placed.
// It is tracked on the Promo tab as Bonus Turnover and saved already settled:
// stake is the bonus amount, profit the guaranteed profit.

const TAG = 'bonus_conversion'

const formatMarketType = (mt) => {
  if (MARKET_TYPE_MAPPING[mt]) return MARKET_TYPE_MAPPING[mt]
  return (mt || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

const formatDate = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })

const round2 = (v) => Math.round(v * 100) / 100

// Same maths as the page: the bonus leg returns winnings only, the hedge is
// sized so both outcomes pay the same.
export const bonusConversion = (bonusAmount, bonusOdds, hedgeOdds) => {
  const amount = Number(bonusAmount), bo = Number(bonusOdds), ho = Number(hedgeOdds)
  if (!(amount > 0) || !(bo > 1) || !(ho > 1)) return { hedgeStake: null, profit: null }
  const winnings = amount * (bo - 1)
  const hedgeStake = winnings / ho
  const profit = Math.min(winnings - hedgeStake, hedgeStake * (ho - 1))
  return { hedgeStake: round2(hedgeStake), profit: round2(profit) }
}

const legsOf = (opp) => ({
  bonus: opp.outcomes?.find(o => o.bet_type === 'BONUS'),
  hedge: opp.outcomes?.find(o => o.bet_type === 'HEDGE'),
})

const trackId = (d) =>
  `${d.sport}_${d.match}_${d.market_type || ''}_${d.selection || ''}_${d.bookmaker || ''}_${d.hedge_bookmaker || ''}`.replace(/[^a-zA-Z0-9_]/g, '_')

const oppTrackId = (opp) => {
  const { bonus, hedge } = legsOf(opp)
  return trackId({ sport: opp.sport, match: opp.match, market_type: opp.market_type, selection: bonus?.selection, bookmaker: bonus?.bookmaker, hedge_bookmaker: hedge?.bookmaker })
}

export function useBonusTrack({ hideOpportunity }) {
  const { user } = useAuth()
  const [tracked, setTracked] = useState(new Set())
  const [opp, setOpp] = useState(null)
  const [form, setForm] = useState({})

  useEffect(() => {
    if (!user?.id) return
    getRecentBetsOfType(user.id, 'PROMO').then(({ data }) => {
      const ids = (data || []).filter(b => b.bet_data?.bet_type_tag === TAG).map(b => trackId(b.bet_data))
      setTracked(new Set(ids))
    })
  }, [user?.id])

  const isTracked = (o) => tracked.has(oppTrackId(o))

  // outcomes carry any odds the user edited on the row
  const open = (o, outcomes, bonusAmount) => {
    const bonus = outcomes.find(x => x.bet_type === 'BONUS')
    const hedge = outcomes.find(x => x.bet_type === 'HEDGE')
    const calc = bonusConversion(bonusAmount, bonus?.odds, hedge?.odds)
    setOpp(o)
    setForm({
      bonus_amount: String(bonusAmount),
      bonus_odds: String(bonus?.odds ?? ''),
      hedge_odds: String(hedge?.odds ?? ''),
      profit: calc.profit != null ? String(calc.profit) : '',
      free_bonus: false,
    })
  }

  const close = () => {
    setOpp(null)
    setForm({})
  }

  const save = async (andHide = false) => {
    if (!user?.id) { alert('Please log in to track bets'); return }
    if (!opp) return
    const { bonus, hedge } = legsOf(opp)
    const { hedgeStake } = bonusConversion(form.bonus_amount, form.bonus_odds, form.hedge_odds)
    const profit = parseFloat(form.profit)
    const stake = parseFloat(form.bonus_amount)
    if (!(stake > 0) || !Number.isFinite(profit)) return

    const settledAt = (opp.commence_time ? new Date(opp.commence_time + 'Z') : new Date()).toISOString()
    const newBet = {
      type: 'PROMO',
      promo_type: 'Bonus Turnover',
      bet_type_tag: TAG,
      sport: opp.sport,
      match: opp.match,
      market_type: opp.market_type,
      selection: bonus?.selection || '',
      bookmaker: bonus?.bookmaker || '',
      odds: parseFloat(form.bonus_odds),
      stake,
      hedge_bookmaker: hedge?.bookmaker || '',
      hedge_selection: hedge?.selection || '',
      hedge_odds: parseFloat(form.hedge_odds),
      hedge_stake: hedgeStake,
      retention_percentage: round2((profit / stake) * 100),
      bonus_received: false,
      bonus_amount: null,
      // A free bonus was never received through a qualifying bet, so it must
      // not count against pending bonus turnover
      free_bonus: !!form.free_bonus,
      status: profit > 0 ? 'won' : 'lost',
      actual_profit: profit,
      commence_time: opp.commence_time ?? null,
      placed_at: new Date().toISOString(),
      settled_at: settledAt,
    }

    const { error } = await saveUserBet(user.id, newBet)
    if (error) { alert('Error saving bet. Please try again.'); return }
    setTracked(prev => new Set(prev).add(oppTrackId(opp)))
    if (andHide && hideOpportunity) hideOpportunity(opp)
    close()
  }

  return { isTracked, open, close, save, opp, form, setForm }
}

export default function TrackBetModal({ opp, form, onFormChange, onConfirm, onConfirmAndHide, onClose, formatSelection }) {
  if (!opp) return null

  const { bonus, hedge } = legsOf(opp)
  const calc = bonusConversion(form.bonus_amount, form.bonus_odds, form.hedge_odds)

  // Changing an input re-derives the profit; the profit field itself can still
  // be overwritten for a price that moved.
  const patch = (fields) => {
    const next = { ...form, ...fields }
    const c = bonusConversion(next.bonus_amount, next.bonus_odds, next.hedge_odds)
    onFormChange({ ...next, profit: c.profit != null ? String(c.profit) : next.profit })
  }

  const valid = Number(form.bonus_amount) > 0 && Number.isFinite(parseFloat(form.profit))
  const sel = (o) => (formatSelection ? formatSelection(o?.selection || '', opp.market_type, opp.player_name) : o?.selection)

  const renderLeg = ({ leg, label, accent, oddsKey, stake }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid #1e2d45' }}>
      {BOOKMAKER_LOGOS[leg?.bookmaker]
        ? <img src={BOOKMAKER_LOGOS[leg.bookmaker]} alt={leg.bookmaker} style={{ width: '24px', height: '24px', borderRadius: '5px', flexShrink: 0 }} />
        : <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 700, minWidth: '24px' }}>{(leg?.bookmaker || '').slice(0, 2).toUpperCase()}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label} · {leg?.bookmaker}</div>
        <div style={{ fontSize: '12px', color: '#cbd5e1', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sel(leg)}</div>
      </div>
      <input
        type="number"
        step="0.01"
        value={form[oddsKey] ?? ''}
        onChange={e => patch({ [oddsKey]: e.target.value })}
        className="ptm-input"
        style={{ width: '72px', padding: '6px 8px', textAlign: 'right' }}
        aria-label={`${label} odds`}
      />
      <span style={{ width: '64px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#f1f5f9', fontVariantNumeric: 'tabular-nums' }}>
        {stake != null ? `$${stake}` : '–'}
      </span>
    </div>
  )

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="ptm-modal">
        <div className="ptm-header">
          <span className="ptm-title">Track Bonus Conversion</span>
          <button className="bc-panel-close" onClick={onClose}>×</button>
        </div>

        <div className="ptm-info-card">
          <div className="ptm-info-body">
            <div className="ptm-player-row">
              <span className="ptm-player-name">{opp.match}</span>
            </div>
            <div className="ptm-info-sub">{formatMarketType(opp.market_type)}</div>
            {opp.commence_time && <div className="ptm-info-meta">{formatDate(opp.commence_time)}</div>}
          </div>
        </div>

        <div className="ptm-form">
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '0 10px', fontSize: '10px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span style={{ width: '72px', textAlign: 'right' }}>Odds</span>
            <span style={{ width: '64px', textAlign: 'right' }}>Stake</span>
          </div>
          {renderLeg({ leg: bonus, label: 'Bonus', accent: '#a78bfa', oddsKey: 'bonus_odds', stake: Number(form.bonus_amount) > 0 ? round2(Number(form.bonus_amount)) : null })}
          {renderLeg({ leg: hedge, label: 'Hedge', accent: '#60a5fa', oddsKey: 'hedge_odds', stake: calc.hedgeStake })}

          <div className="ptm-form-row">
            <div className="ptm-field">
              <label className="ptm-label">Bonus amount ($)</label>
              <input type="number" step="1" value={form.bonus_amount ?? ''} onChange={e => patch({ bonus_amount: e.target.value })} className="ptm-input" />
            </div>
            <div className="ptm-field">
              <label className="ptm-label">Profit ($)</label>
              <input type="number" step="0.01" value={form.profit ?? ''} onChange={e => onFormChange({ ...form, profit: e.target.value })} className="ptm-input" style={{ color: '#34d399', fontWeight: 600 }} />
            </div>
          </div>

          <label className="ptm-check">
            <input type="checkbox" checked={!!form.free_bonus} onChange={e => onFormChange({ ...form, free_bonus: e.target.checked })} />
            <span>Free bonus (no qualifying bet)</span>
          </label>
        </div>

        <div className="ptm-footer">
          <button className="bc-btn ghost" onClick={onClose}>Cancel</button>
          <div className="ptm-footer-right">
            <button className="bc-btn danger" onClick={onConfirmAndHide} disabled={!valid}>Track & Hide</button>
            <button className="bc-btn primary" onClick={onConfirm} disabled={!valid}>Track</button>
          </div>
        </div>
      </div>
    </>
  )
}
